import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, ArrowUpRight } from "lucide-react";

const POLICY = [
  {
    n: "01",
    title: "Delivered, insured, hand-inspected",
    body: "Every piece leaves our showroom in impeccable condition and arrives at your address safely and on schedule. As standard, we do not accept returns — but our team is always a call away. For any query, kindly reach out via the \"Reach out to Us\" panel and we'll attend to it personally.",
  },
  {
    n: "02",
    title: "Damage on arrival — resolved with priority",
    body: "In the rare event a piece arrives damaged, please write to us within 24 hours with clear photographs. We treat this as our own responsibility and will restore, replace, or refund without delay.",
  },
  {
    n: "03",
    title: "When something isn't quite right",
    body: "We understand there are moments when a piece may not sit as expected in your room. Should this happen, do reach out to us. We'll assess your location, the product and the circumstances — and let you know whether a return can be arranged, occasionally at a nominal additional charge.",
  },
  {
    n: "04",
    title: "Transparent return logistics",
    body: "Where a return is offered, we arrange a courier to collect the product from your doorstep and return it safely to our showroom. The associated cost — always modest — is shared with you in writing before we proceed. No surprises, no fine print.",
  },
];

export default function ReturnPolicy() {
  return (
    <div className="pt-32 pb-24 container-x" data-testid="return-policy-page">
      <div className="max-w-3xl mb-16">
        <div className="num-badge mb-4"><span>Return Policy</span></div>
        <h1 className="font-display leading-[0.95] tracking-[-0.02em]" style={{ fontSize: "clamp(3rem, 6vw, 6rem)", fontWeight: 400 }}>
          Delivered with <em className="gold-text" style={{ fontWeight: 500 }}>care.</em>
        </h1>
        <p className="mt-6 max-w-2xl leading-relaxed" style={{ color: "rgba(15,12,8,0.65)", fontFamily: "'Manrope', sans-serif" }}>
          Every order from Batra Electronics is hand-inspected in our Dehradun showroom, insured in transit, and delivered by white-glove logistics. Below is the full extent of our return policy — considered, transparent, and treated case-by-case where thought is required.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {POLICY.map((p) => (
            <div key={p.n} className="card-luxury p-8 md:p-10" data-testid={`policy-item-${p.n}`}>
              <div className="flex gap-6">
                <div className="shrink-0">
                  <div className="font-editorial text-4xl md:text-5xl leading-none" style={{ color: "#8A6A0F", fontStyle: "italic", fontWeight: 400 }}>{p.n}</div>
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="font-display text-2xl md:text-3xl tracking-[-0.01em]" style={{ color: "var(--ink)", fontWeight: 500 }}>{p.title}</h3>
                  <p className="mt-3 leading-[1.75]" style={{ color: "rgba(15,12,8,0.7)", fontFamily: "'Manrope', sans-serif" }}>{p.body}</p>
                </div>
              </div>
            </div>
          ))}

          <div className="mt-8 text-xs uppercase tracking-[0.24em]" style={{ color: "rgba(15,12,8,0.4)", fontFamily: "'JetBrains Mono', monospace" }}>
            Last updated · February 2026
          </div>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-32 self-start" data-testid="policy-reach-us">
          <div className="glass rounded-2xl p-6">
            <div className="kicker mb-3">Reach out to Us</div>
            <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(15,12,8,0.65)", fontFamily: "'Manrope', sans-serif" }}>
              For any return, damage or query — please contact us directly. We respond within one business day.
            </p>
            <a href="tel:+918018911999" className="flex items-start gap-3 py-3 border-t border-black/8 hover:opacity-70 transition" data-testid="policy-phone">
              <Phone className="w-4 h-4 mt-1 shrink-0" style={{ color: "#8A6A0F" }} />
              <div className="flex-1 min-w-0">
                <div className="kicker text-[9px] mb-1">Phone</div>
                <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>+91 80189 11999</div>
              </div>
              <ArrowUpRight className="w-3.5 h-3.5 opacity-40 mt-1" />
            </a>
            <a href="https://wa.me/918018911999" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 py-3 border-t border-black/8 hover:opacity-70 transition" data-testid="policy-whatsapp">
              <MessageCircle className="w-4 h-4 mt-1 shrink-0" style={{ color: "#8A6A0F" }} />
              <div className="flex-1 min-w-0">
                <div className="kicker text-[9px] mb-1">WhatsApp</div>
                <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>Chat instantly</div>
              </div>
              <ArrowUpRight className="w-3.5 h-3.5 opacity-40 mt-1" />
            </a>
            <a href="mailto:batraelectronics55@gmail.com" className="flex items-start gap-3 py-3 border-t border-black/8 hover:opacity-70 transition" data-testid="policy-email">
              <Mail className="w-4 h-4 mt-1 shrink-0" style={{ color: "#8A6A0F" }} />
              <div className="flex-1 min-w-0">
                <div className="kicker text-[9px] mb-1">Email</div>
                <div className="text-sm font-medium break-all" style={{ color: "var(--ink)" }}>batraelectronics55@gmail.com</div>
              </div>
              <ArrowUpRight className="w-3.5 h-3.5 opacity-40 mt-1 shrink-0" />
            </a>
          </div>

          <Link to="/contact" className="btn-ghost-outline w-full justify-center text-sm" data-testid="policy-contact-cta">
            Full contact details <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </aside>
      </div>
    </div>
  );
}
